// The cart, and the one thing about it that must survive a crash.
//
// Everything here is local until checkout. That is what makes the till fast:
// a tap changes a number on screen, not a round trip. The server reprices the
// whole order at checkout anyway, so nothing in this file is trusted -- it is
// a preview that happens to be right because it runs the same formula.
//
// Two things are written to localStorage, and they are not the same thing:
//
//   draft:<till id>             the cart, so a reload does not lose the order
//   pending_checkout:<till id>  a sale that was sent and never answered
//
// The second is the important one. A till that posts a checkout and loses
// power before the response arrives has no way to know whether the order
// exists. The key is written *before* the request and cleared *after* it, so
// whatever happens in between leaves a record; on the next boot the till sends
// the same key again and the server answers with the order it already made.

import {
	discountMinor,
	perKgChoiceMinor,
	recomputeTotals,
	type DiscountKind,
	type Line,
	type LineModifier,
	type LineWeight,
	type OrderDiscount,
	type Totals,
} from '../domain/pricing';
import { readStoredDraft, readStoredPending } from './stored';

export type OrderType = 'dine_in' | 'takeaway' | 'delivery';

/**
 * One line of the cart.
 *
 * The shape the pricing mirror already prices, with the two optional fields
 * made required: a cart line always has a list of choices and a note, even when
 * both are empty, so nothing downstream has to handle "absent" and "empty"
 * separately.
 */
export interface DraftLine extends Line {
	/** Local id, so a line can be edited and removed without an index. */
	key: string;
	modifiers: LineModifier[];
	note: string;
}

export interface Draft {
	lines: DraftLine[];
	type: OrderType;
	customerName: string;
	customerPhone: string;
	deliveryAddress: string;
	/** Floor, flat or a nearby shop, printed under the address. */
	deliveryLandmark: string;
	deliveryAreaId: string;
	note: string;
	/** Names this sale. Every retry carries it; a new sale mints a new one. */
	idempotencyKey: string;
	startedBy: string;
	/**
	 * The one discount the order is given (UC-35), or null. One from the
	 * owner's list keeps its id and, for the cart to show, its name, kind and
	 * value as the menu last gave them; a manager's typed one is a kind and a
	 * value. A cart an older bundle kept has none.
	 */
	discount: OrderDiscount | null;
	/**
	 * The delivery fee the cart last showed, 0 for an order with none. Only
	 * the lock screen reads it: it reads the stored cart before anybody signs
	 * in, so it cannot ask the server, and the cart's discount comes off the
	 * subtotal and this fee together (UC-35). Nothing sends it; the server
	 * prices the fee itself. A cart an older bundle kept has none.
	 */
	deliveryFeeMinor: number;
	/**
	 * The table a dine-in sits at (UC-39), '' for none, and its name as the
	 * till's list last gave it, for the cart to show. The draft keeps it
	 * through a type switch, as it keeps the delivery details; only a dine-in
	 * sends it. A cart an older bundle kept has none.
	 */
	tableId: string;
	tableName: string;
	/**
	 * The driver a delivery goes with (UC-4.R6, T33), '' for none, and his
	 * name as the till's list last gave it, for the cart to show. The draft
	 * keeps him through a type switch, as it keeps the delivery details; only
	 * a delivery sends him. A cart an older bundle kept has none, and its Send
	 * asks which driver.
	 */
	driverId: string;
	driverName: string;
	/**
	 * The open order these lines go on (#348), or '' for a cart not sent
	 * yet. Every order is sent before it is paid: the first Send places it
	 * open, and from then the cart holds only the lines not sent yet, which
	 * the next Send adds to it. The till's order store holds the order and
	 * its sent lines; a lock, a reload or another cashier's unlock keeps
	 * this, so any cashier on the till goes on with the order. A cart an
	 * older bundle kept has none, and is a new order.
	 */
	openOrderId: string;
	/**
	 * A later Send of these lines went out and no answer has said what became
	 * of it (#348): the store may have added them already. Written before the
	 * request, so a reload keeps it; the cart holds still until Retry, under
	 * the same key, gets an answer. A cart an older bundle kept has none.
	 */
	additionUnsure: boolean;
}

/** One of the shop's tables, as the till's list gives it (UC-39). */
export interface ListedTable {
	id: string;
	name: string;
}

/** One of the shop's active drivers, as the till's list gives him (UC-4.R6). */
export interface ListedDriver {
	id: string;
	name: string;
}

/** A checkout that was sent and never answered. */
export interface PendingCheckout {
	idempotencyKey: string;
	body: unknown;
	lineCount: number;
	totalMinor: number;
}

/** One discount of the owner's list, as the menu read gives it. */
export interface ListedDiscount {
	id: string;
	name: string;
	kind: DiscountKind;
	value: number;
}

export function emptyDraft(startedBy = ''): Draft {
	return {
		lines: [],
		type: 'takeaway',
		customerName: '',
		customerPhone: '',
		deliveryAddress: '',
		deliveryLandmark: '',
		deliveryAreaId: '',
		note: '',
		idempotencyKey: newKey(),
		startedBy,
		discount: null,
		deliveryFeeMinor: 0,
		tableId: '',
		tableName: '',
		driverId: '',
		driverName: '',
		openOrderId: '',
		additionUnsure: false,
	};
}

// crypto.randomUUID is in every browser this till runs on. The fallback is for
// a non-secure context, where it is absent: a weaker id is still unique enough
// to name one sale on one till, and refusing to sell would be worse.
function newKey(): string {
	try {
		return crypto.randomUUID();
	} catch {
		return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
	}
}

let draft = $state<Draft>(emptyDraft());
let pending = $state<PendingCheckout | null>(null);
let tillId = '';

/** What the cart holds now. */
export function currentDraft(): Draft {
	return draft;
}

/** The sale that was sent and never answered, if there is one. */
export function currentPending(): PendingCheckout | null {
	return pending;
}

/**
 * Point the store at a till and read back whatever it left behind.
 *
 * Keyed by till because one browser profile is one till; keying by nothing
 * would let a re-paired till inherit the previous one's cart.
 */
export function attachDraft(id: string, startedBy: string): void {
	tillId = id;
	// A record an older bundle kept may name a key as it was before #123
	// renamed it (stored.ts).
	const saved = readStoredDraft(read<Draft>(draftKey()));
	// A draft kept before landmarks existed has none, one kept before
	// discounts (UC-35) has no discount, one kept before tables (UC-39)
	// sits at none, and one kept before drivers (UC-4.R6) names none.
	draft =
		saved && Array.isArray(saved.lines)
			? {
					...saved,
					deliveryLandmark: saved.deliveryLandmark ?? '',
					discount: saved.discount ?? null,
					deliveryFeeMinor: feeOf(saved),
					tableId: typeof saved.tableId === 'string' ? saved.tableId : '',
					tableName: typeof saved.tableName === 'string' ? saved.tableName : '',
					driverId: typeof saved.driverId === 'string' ? saved.driverId : '',
					driverName: typeof saved.driverName === 'string' ? saved.driverName : '',
					openOrderId: typeof saved.openOrderId === 'string' ? saved.openOrderId : '',
					additionUnsure: saved.additionUnsure === true,
				}
			: emptyDraft(startedBy);
	pending = readStoredPending(read<PendingCheckout>(pendingKey()));
}

/** The fee a stored cart kept, or 0 for one an older bundle kept with none. */
function feeOf(saved: { deliveryFeeMinor?: unknown }): number {
	const fee = saved.deliveryFeeMinor;
	return typeof fee === 'number' && Number.isSafeInteger(fee) && fee > 0 ? fee : 0;
}

function draftKey(): string {
	return `draft:${tillId}`;
}

function pendingKey(): string {
	return `pending_checkout:${tillId}`;
}

function read<T>(key: string): T | null {
	try {
		const raw = localStorage.getItem(key);
		return raw ? (JSON.parse(raw) as T) : null;
	} catch {
		// A private window, cleared site data, or storage the browser refuses.
		// An empty cart is the right answer to all three.
		return null;
	}
}

function write(key: string, value: unknown): void {
	try {
		if (value === null) localStorage.removeItem(key);
		else localStorage.setItem(key, JSON.stringify(value));
	} catch {
		// Storage is a convenience here, not the sale. A till that cannot
		// persist still sells; it only loses the cart on a reload.
	}
}

function save(): void {
	if (!tillId) return;
	write(draftKey(), draft.lines.length > 0 || draft.openOrderId ? draft : null);
}

/**
 * What a weighed portion's grams cost at its kilo price (#917), as the pricing
 * mirror takes it: the line keeps it as its unitBaseMinor, so an older bundle
 * that reads the cart without its weight prices it alike. Through
 * recomputeTotals, so the screen holds no second copy of the rule; 0 for a
 * weight the mirror refuses, which the editor never lets through.
 */
export function weighedBase(weight: LineWeight): number {
	try {
		return recomputeTotals([{ itemId: '', name: '', unitBaseMinor: 0, qty: 1, weight }], 0, null, 0)
			.subtotalMinor;
	} catch {
		return 0;
	}
}

/**
 * Each per-kg choice's amount for the line's grams (#935, UC-3.R11), kept as
 * its priceDeltaMinor beside its kind and rate, so an older bundle that adds
 * priceDeltaMinor prices the line as this one does. Through recomputeTotals's
 * own rule, so the screen holds no second copy of it.
 */
function keepAmounts(line: { modifiers: LineModifier[]; weight?: LineWeight }): boolean {
	let changed = false;
	for (const modifier of line.modifiers) {
		if (modifier.kind !== 'perKg' || !line.weight) continue;
		const amount = perKgChoiceMinor(modifier.perKgMinor ?? 0, line.weight.grams);
		if (amount !== modifier.priceDeltaMinor) {
			modifier.priceDeltaMinor = amount;
			changed = true;
		}
	}
	return changed;
}

/** Add an item, or add one to the line that already matches it.
 *
 * Tapping a tile twice means two of that item, which is only true when nothing
 * was customised on the first: a Pizza with Large and a Pizza with Small are
 * two lines, not a quantity of two.
 */
export function addItem(itemId: string, name: string, unitBaseMinor: number): void {
	const plain = draft.lines.find(
		(line) =>
			line.itemId === itemId && !line.weight && line.modifiers.length === 0 && line.note === '',
	);
	if (plain) {
		if (plain.qty < 999) plain.qty += 1;
	} else {
		draft.lines.push({
			key: newKey(),
			itemId,
			name,
			unitBaseMinor,
			modifiers: [],
			qty: 1,
			note: '',
		});
	}
	save();
}

/**
 * Add a line the editor built, which is what Done does for an item that asked
 * its questions before it reached the cart.
 */
export function addLine(line: Omit<DraftLine, 'key'>): void {
	const added = { ...line, key: newKey() };
	keepAmounts(added);
	draft.lines.push(added);
	save();
}

/**
 * Replace one line, which is what the editor's Done does. A weighed portion
 * (#917) takes its new grams and the base they cost with them.
 */
export function updateLine(
	key: string,
	change: {
		modifiers: LineModifier[];
		qty: number;
		note: string;
		weight?: LineWeight;
		unitBaseMinor?: number;
	},
): void {
	const line = draft.lines.find((l) => l.key === key);
	if (!line) return;
	line.modifiers = change.modifiers;
	line.qty = change.qty;
	line.note = change.note;
	if (line.weight && change.weight && change.unitBaseMinor !== undefined) {
		line.weight = change.weight;
		line.unitBaseMinor = change.unitBaseMinor;
	}
	keepAmounts(line);
	save();
}

/**
 * Take a line out of the cart. The last line takes the discount with it, since
 * a discount on nothing is no sale, and the discount it took is returned so
 * that line's Undo puts the line back with it: a manager's typed discount
 * always, a list discount while the list the till last read still offers it
 * (and as it left when no list has been read), see restoreLine and followList.
 */
export function removeLine(key: string): OrderDiscount | null {
	draft.lines = draft.lines.filter((line) => line.key !== key);
	let took: OrderDiscount | null = null;
	if (draft.lines.length === 0 && draft.discount) {
		took = draft.discount;
		draft.discount = null;
	}
	save();
	return took;
}

/**
 * Put a removed line back where it was, which is what Undo does, with the
 * discount it took when it was the last line (UC-35).
 *
 * The owner may have removed or edited that discount while the Undo waited,
 * so it comes back only as `offered`, the owner's list the till last read,
 * still holds it: one the list lacks stays off, and one the list changed
 * comes back as the list has it, as a menu read would do (followList).
 * Without a list, from a menu not read yet or a server that predates
 * discounts, it comes back as it left, and the next read decides. True when
 * the list took it off, so the till can say why the total did not come back.
 */
export function restoreLine(
	line: DraftLine,
	at: number,
	discount: OrderDiscount | null = null,
	offered?: ListedDiscount[] | null,
): boolean {
	draft.lines.splice(Math.min(at, draft.lines.length), 0, line);
	let removed = false;
	if (discount && !draft.discount) {
		draft.discount = discount;
		removed = followList(offered).removed;
	}
	save();
	return removed;
}

/**
 * Give the order its one discount, or none (UC-35). A new one replaces the
 * last: an order takes one discount, never two.
 */
export function setDiscount(discount: OrderDiscount | null): void {
	draft.discount = discount;
	save();
}

export function setType(type: OrderType): void {
	draft.type = type;
	save();
}

/**
 * Seat the order at one of the shop's tables, or at none (UC-39). Only a
 * dine-in sends it; a type switch keeps it, so switching back is free.
 */
export function setTable(table: ListedTable | null): void {
	draft.tableId = table?.id ?? '';
	draft.tableName = table?.name ?? '';
	save();
}

/**
 * Bring the cart's table up to date with the shop's list just read (UC-39):
 * a renamed table shows its new name, and one the owner removed comes off the
 * cart, as the server would refuse it. True when that happened, so the till
 * can ask which table again.
 */
export function followTables(listed: ListedTable[]): boolean {
	if (!draft.tableId) return false;
	const now = listed.find((t) => t.id === draft.tableId);
	if (!now) {
		setTable(null);
		return true;
	}
	if (now.name !== draft.tableName) {
		draft.tableName = now.name;
		save();
	}
	return false;
}

/**
 * Send the delivery with one of the shop's drivers, or with none (UC-4.R6,
 * T33). Only a delivery sends him; a type switch keeps him.
 */
export function setDriver(driver: ListedDriver | null): void {
	draft.driverId = driver?.id ?? '';
	draft.driverName = driver?.name ?? '';
	save();
}

/**
 * Bring the cart's driver up to date with the shop's list just read: a
 * renamed driver shows his new name, and one a manager or the owner removed
 * comes off the cart, as the store would refuse him (DRIVER_INACTIVE). True
 * when that happened, so the till can ask which driver again.
 */
export function followDrivers(listed: ListedDriver[]): boolean {
	if (!draft.driverId) return false;
	const now = listed.find((d) => d.id === draft.driverId);
	if (!now) {
		setDriver(null);
		return true;
	}
	if (now.name !== draft.driverName) {
		draft.driverName = now.name;
		save();
	}
	return false;
}

/**
 * Keep the delivery fields when the type changes.
 *
 * A cashier who switches to takeaway and back gets the same area, phone and
 * address, because they are still stored: only the fee is dropped, and the fee
 * is not stored here at all. The server decides it from the type.
 */
export function setDelivery(fields: {
	deliveryAreaId?: string;
	customerPhone?: string;
	deliveryAddress?: string;
	deliveryLandmark?: string;
	customerName?: string;
}): void {
	if (fields.deliveryAreaId !== undefined) draft.deliveryAreaId = fields.deliveryAreaId;
	if (fields.customerPhone !== undefined) draft.customerPhone = fields.customerPhone;
	if (fields.deliveryAddress !== undefined) draft.deliveryAddress = fields.deliveryAddress;
	if (fields.deliveryLandmark !== undefined) draft.deliveryLandmark = fields.deliveryLandmark;
	if (fields.customerName !== undefined) draft.customerName = fields.customerName;
	save();
}

/**
 * Bring the cart's prices up to date with the menu just read.
 *
 * A line keeps a snapshot of what it costs so the cart can price itself between
 * menu reads. Without this, an owner who repriced an item mid-order would leave
 * the cashier looking at the old total for as long as the cart stayed open, and
 * the checkout would refuse a total the screen still showed. So every menu read
 * refreshes what is in the cart, and an item that has left the menu keeps the
 * price it was added at -- the server refuses it either way, and guessing zero
 * would put a free line on the screen.
 *
 * A discount is the other way round (UC-35): one the owner removed comes off
 * the cart, since the server would refuse it, and the cart must not show a
 * total nobody will be charged. True when that happened, so the till can say
 * why the total moved.
 */
export function repriceDraft(menu: {
	items?: { id: string; priceMinor: number }[] | null;
	groups?: { modifiers?: { id: string; priceDeltaMinor: number }[] | null }[] | null;
	discounts?: ListedDiscount[] | null;
}): boolean {
	// Plain records, not Maps: these are a scratch lookup built and thrown away
	// inside this call, and nothing reads them reactively.
	const prices: Record<string, number> = {};
	for (const item of menu.items ?? []) prices[item.id] = item.priceMinor;
	const deltas: Record<string, number> = {};
	for (const group of menu.groups ?? []) {
		for (const modifier of group.modifiers ?? []) deltas[modifier.id] = modifier.priceDeltaMinor;
	}

	let changed = false;
	for (const line of draft.lines) {
		const price = prices[line.itemId];
		// A weighed portion's price is its kilo price (#917): the line keeps
		// it beside the base its grams cost there, which an older bundle reads.
		if (line.weight) {
			if (price !== undefined && price !== line.weight.perKgMinor) {
				line.weight.perKgMinor = price;
				line.unitBaseMinor = weighedBase(line.weight);
				changed = true;
			}
		} else if (price !== undefined && price !== line.unitBaseMinor) {
			line.unitBaseMinor = price;
			changed = true;
		}
		for (const modifier of line.modifiers) {
			const delta = deltas[modifier.id];
			if (delta === undefined) continue;
			// A per-kg choice's menu price is its rate (#935): the line keeps
			// the rate, and its amount for the grams follows.
			if (modifier.kind === 'perKg') {
				if (delta !== modifier.perKgMinor) {
					modifier.perKgMinor = delta;
					changed = true;
				}
			} else if (delta !== modifier.priceDeltaMinor) {
				modifier.priceDeltaMinor = delta;
				changed = true;
			}
		}
		if (keepAmounts(line)) changed = true;
	}
	const followed = followList(menu.discounts);
	if (changed || followed.changed) save();
	return followed.removed;
}

/**
 * Bring the cart's discount up to date with the owner's list (UC-35).
 *
 * A discount from the owner's list follows the owner's edits, as a price
 * does, so the cart shows what the checkout will take, and one the owner
 * removed comes off. A manager's typed one is nobody's to edit. A list that is
 * not there at all, from a server that predates it, says nothing about the
 * discount. The caller saves.
 */
function followList(listed: ListedDiscount[] | null | undefined): {
	changed: boolean;
	removed: boolean;
} {
	const given = draft.discount;
	if (!given?.discountId || !Array.isArray(listed)) return { changed: false, removed: false };
	const now = listed.find((d) => d.id === given.discountId);
	if (!now) {
		draft.discount = null;
		return { changed: true, removed: true };
	}
	if (now.name === given.name && now.kind === given.kind && now.value === given.value) {
		return { changed: false, removed: false };
	}
	draft.discount = { discountId: now.id, name: now.name, kind: now.kind, value: now.value };
	return { changed: true, removed: false };
}

/**
 * Keep the delivery fee the cart shows now, for the lock screen to read.
 *
 * The till calls this whenever the fee it shows moves: a quote's answer, an
 * area picked, a takeaway's 0.
 */
export function noteDeliveryFee(feeMinor: number): void {
	if (draft.deliveryFeeMinor === feeMinor) return;
	draft.deliveryFeeMinor = feeMinor;
	save();
}

/**
 * What the cart costs before the server has priced it, its discount taken off
 * with the currency's minor digits, as the server does.
 */
export function draftTotals(deliveryMinor: number, minorDigits: number): Totals | null {
	try {
		return recomputeTotals(draft.lines, deliveryMinor, draft.discount, minorDigits);
	} catch {
		// A line the editor should have blocked. The cart shows no total rather
		// than a wrong one, and the server refuses the same order.
		return null;
	}
}

/**
 * The request body a quote and a checkout share.
 *
 * `startedBy` is deliberately not here. It belongs to a placement and the quote
 * route does not declare it, so sending it there is a validation failure -- and
 * a quote that always fails is a delivery fee that never arrives.
 */
export function draftBody(): Record<string, unknown> {
	const body: Record<string, unknown> = {
		type: draft.type,
		// A weighed portion names its grams (#917); a piece line sends the
		// body it always did.
		lines: draft.lines.map((line) => ({
			itemId: line.itemId,
			qty: line.qty,
			modifiers: line.modifiers.map((m) => m.id),
			note: line.note || undefined,
			grams: line.weight?.grams,
		})),
	};
	if (draft.type === 'delivery') {
		if (draft.deliveryAreaId) body.deliveryAreaId = draft.deliveryAreaId;
		if (draft.customerPhone) body.customerPhone = draft.customerPhone;
		if (draft.deliveryAddress) body.deliveryAddress = draft.deliveryAddress;
		if (draft.deliveryLandmark) body.deliveryLandmark = draft.deliveryLandmark;
		if (draft.customerName) body.customerName = draft.customerName;
	}
	if (draft.note) body.note = draft.note;
	// Only when the cart has one, so a sale with none sends the body it always
	// did. One from the list goes by its id alone: the server reads its name,
	// kind and value from the owner's list (UC-35).
	if (draft.discount) {
		body.discount = draft.discount.discountId
			? { discountId: draft.discount.discountId }
			: { kind: draft.discount.kind, value: draft.discount.value };
	}
	// A dine-in's table (UC-39), only when it has one, so a sale with none
	// sends the body it always did. A takeaway or a delivery never names one,
	// though the cart keeps it through a type switch.
	if (draft.type === 'dine_in' && draft.tableId) body.tableId = draft.tableId;
	// A delivery's driver (UC-4.R6), only when it has one, last, so a sale
	// with none sends the body it always did.
	if (draft.type === 'delivery' && draft.driverId) body.driverId = draft.driverId;
	return body;
}

/**
 * Record that a checkout is about to be sent.
 *
 * Written before the request, so a till that dies mid-flight still knows a sale
 * may exist. Everything needed to send it again is kept, because the retry must
 * be byte-for-byte the same request: a different body under the same key is a
 * conflict, not a retry.
 */
export function beginCheckout(body: unknown, totalMinor: number): PendingCheckout {
	const record: PendingCheckout = {
		idempotencyKey: draft.idempotencyKey,
		body,
		lineCount: draft.lines.length,
		totalMinor,
	};
	pending = record;
	write(pendingKey(), record);
	return record;
}

/** Record that the server answered, whatever it answered. */
export function endCheckout(): void {
	pending = null;
	write(pendingKey(), null);
}

/** What a till left behind, for the lock screen to show. */
export interface StoredSummary {
	lineCount: number;
	/**
	 * An unsent sale's total as it was sent. A cart's subtotal and the
	 * delivery fee it last showed, before its discount.
	 */
	totalMinor: number;
	/** True when a checkout was sent and never answered. */
	unsent: boolean;
	/**
	 * The cart's discount, for the lock screen to take off with the account's
	 * minor digits, which this read does not know. Absent with none, and for
	 * an unsent sale, whose total was already taken.
	 */
	discount?: { kind: DiscountKind; value: number };
}

/**
 * The total the lock screen shows for what a till left behind: an unsent
 * sale's as it was sent, or the cart's as the cart showed it, its discount
 * taken off the subtotal and the delivery fee together, by the cart's own rule
 * (UC-35).
 */
export function waitingTotal(summary: StoredSummary, minorDigits: number): number {
	if (!summary.discount) return summary.totalMinor;
	const { kind, value } = summary.discount;
	return summary.totalMinor - discountMinor(summary.totalMinor, kind, value, minorDigits);
}

/**
 * Peek at what this till has waiting, without attaching the store.
 *
 * The lock screen shows it so a cashier coming back to a covered till knows
 * there is an order underneath before they type anything -- and, more
 * importantly, so a till that was cut off mid-sale says so rather than looking
 * like a fresh one. Read-only on purpose: the lock screen owns none of this.
 */
export function storedSummary(tillId: string): StoredSummary | null {
	const waiting = read<PendingCheckout>(`pending_checkout:${tillId}`);
	if (waiting) {
		return { lineCount: waiting.lineCount, totalMinor: waiting.totalMinor, unsent: true };
	}
	const saved = read<Draft>(`draft:${tillId}`);
	if (!saved || !Array.isArray(saved.lines) || saved.lines.length === 0) return null;
	try {
		const totals = recomputeTotals(saved.lines, feeOf(saved), null, 0);
		const summary: StoredSummary = {
			lineCount: saved.lines.length,
			totalMinor: totals.totalMinor,
			unsent: false,
		};
		if (saved.discount) {
			summary.discount = { kind: saved.discount.kind, value: saved.discount.value };
		}
		return summary;
	} catch {
		// A draft the editor should have blocked. Its count is still worth
		// showing; a total nobody can compute is not.
		return { lineCount: saved.lines.length, totalMinor: 0, unsent: false };
	}
}

/** Clear the cart and mint a key for the next sale. */
export function clearDraft(): void {
	draft = emptyDraft(draft.startedBy);
	write(draftKey(), null);
}

/**
 * The cart's lines are on the order now (#348): its first Send placed it
 * open, or a later Send added them. The cart keeps only the order, under a
 * new key for its next write; what it was given, its type, its discount and
 * its delivery details, is the order's from here.
 */
export function cartSent(orderId: string): void {
	draft = { ...emptyDraft(draft.startedBy), type: draft.type, openOrderId: orderId };
	save();
}

/**
 * Go on with an open order of this till (#348): Orders opens it on Sell. Only
 * a cart with no line waiting to be sent opens another order, so nothing is
 * lost.
 */
export function openOrder(orderId: string, type: OrderType): boolean {
	if (draft.lines.length > 0) return false;
	draft = { ...emptyDraft(draft.startedBy), type, openOrderId: orderId };
	save();
	return true;
}

/**
 * The open order the cart named is no longer open (#348): paid or voided
 * meanwhile. Its lines not sent yet stay, as a new cart, under a new key: an
 * addition to the old order may have used the one it held.
 */
export function forgetOpenOrder(): void {
	draft.openOrderId = '';
	draft.idempotencyKey = newKey();
	draft.additionUnsure = false;
	save();
}

/**
 * A new key for the cart's next write, when the store says the key it holds
 * named another (#348): its lines are kept, to be sent under the new one.
 */
export function renewKey(): void {
	draft.idempotencyKey = newKey();
	draft.additionUnsure = false;
	save();
}

/**
 * A later Send is on its way, or its answer said what became of it (#348).
 * Kept with the cart, so a reload still holds the cart still while the store
 * may have added its lines.
 */
export function noteAddition(unsure: boolean): void {
	draft.additionUnsure = unsure;
	save();
}
